"use client";
import Container from "../container";
import styles from "./footer.module.css";
import Logo from "../Logo";
import Link from "next/link";
import loveIcon from "@/images/love.svg";
import aavattoLogo from "@/public/Aavatto.svg";
import Image from "next/image";
import MobileBanner from "../mobile-banner/mobilebanner";
import { getRemixIcon } from "@/lib/remixIcon";
import CMSLink from "../common/CMSLink";
import AppImage from "../common/AppImage";
import { usePathname, useSearchParams } from "next/navigation";
import {
  buildDynamicContext,
  resolveDynamicVariables,
} from "@/lib/replaceDynamicValues";
import { useLayout } from "@/lib/LayoutContext";
import { EXTERNAL_REL } from "@/lib/externalLink";

/* ── Helper: render a column of links ─────────────────────── */
function FooterLinkColumn({ title, links }) {
  const items = (links || []).filter((l) => l?.label && l?.url);
  if (!title && items.length === 0) return null;

  return (
    <div className={styles.linkCol}>
      {title && <strong className={styles.colTitle}>{title}</strong>}
      {items.map((link, i) => (
        <CMSLink
          key={i}
          config={link}
          useIn="header"
          className={styles.footerLink}
          title={link.label}
        />
      ))}
    </div>
  );
}

/* ── Helper: social icon row ───────────────────────────────── */
function SocialRow({ items }) {
  const filtered = (items || []).filter((s) => s?.icon && s?.url);
  if (filtered.length === 0) return null;

  return (
    <div className={styles.socialRow}>
      {filtered.map((social, i) => {
        const Icon = getRemixIcon(social.icon);
        if (!Icon) return null;
        return (
          <CMSLink
            key={i}
            config={{ url: { url: social.url, type: social.link_type } }}
            className={styles.socialIcon}
            aria-label={social.label || social.icon}
          >
            <Icon size={20} />
          </CMSLink>
        );
      })}
    </div>
  );
}

/* ── Helper: build a tel:/mailto:/wa.me href for contact links ───── */
function getContactHref(item) {
  const url = (item?.url || "").trim();
  const label = (item?.label || "").toLowerCase();
  const icon = (item?.icon || "").toLowerCase();
  const value = (item?.value || "").trim();

  // 1. If explicit full http/https/wa.me/mailto/tel link is provided in url
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("mailto:") ||
    url.startsWith("tel:")
  ) {
    return url;
  }
  if (url.startsWith("wa.me")) {
    return `https://${url}`;
  }

  // 2. WhatsApp specific check
  if (label.includes("whatsapp") || icon.includes("whatsapp")) {
    if (url) return url.startsWith("http") ? url : `https://${url}`;
    const cleanNum = value.replace(/[^0-9]/g, "");
    return `https://wa.me/${cleanNum}`;
  }

  // 3. Email check
  if (
    label.includes("email") ||
    icon.includes("mail") ||
    value.includes("@") ||
    url.includes("@")
  ) {
    const email = url.includes("@") ? url : value;
    return email.startsWith("mailto:") ? email : `mailto:${email}`;
  }

  // 4. Phone check
  if (
    label.includes("phone") ||
    label.includes("call") ||
    label.includes("tel") ||
    icon.includes("phone")
  ) {
    const phone = url || value;
    return phone.startsWith("tel:")
      ? phone
      : `tel:${phone.replace(/[\s-]/g, "")}`;
  }

  // 5. If url is specified
  if (url) {
    return url;
  }

  // 6. Generic phone number fallback
  if (/^[+\d][\d\s-]+$/.test(value)) {
    return "tel:" + value.replace(/[\s-]/g, "");
  }

  return value;
}

/* ── Helper: contact info rows ─────────────────────────────── */
function ContactRow({ items }) {
  const filtered = (items || []).filter((c) => c?.value || c?.url);
  if (filtered.length === 0) return null;

  return (
    <div className={styles.contactList}>
      {filtered.map((item, i) => {
        const Icon =
          getRemixIcon(item.icon) ||
          (item.value?.includes("@") ||
          item.label?.toLowerCase().includes("email")
            ? getRemixIcon("mail-check-line") || getRemixIcon("mail-line")
            : getRemixIcon("phone-line"));
        return (
          <CMSLink
            key={i}
            config={{ url: { url: getContactHref(item), type: "external" } }}
            className={styles.contactItem}
          >
            {Icon && <Icon size={15} className={styles.contactIcon} />}
            <span>{item.value || item.url}</span>
          </CMSLink>
        );
      })}
    </div>
  );
}

/* ── Main footer ───────────────────────────────────────────── */
export default function Footer({ data, mobileAppCtaData }) {
  const footerData = resolveDynamicVariables(
    data?.footer,
    buildDynamicContext(),
  );
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { hideFooter, totalBottomOffset } = useLayout();
  if (!footerData) return null;
  if (hideFooter) return null;
  if (searchParams.get("is_application") === "true") return null;
  if (pathname.includes("/book") || pathname.includes("/search")) return null;

  const segments = pathname.split("/").filter(Boolean);

  const extraEventPadding =
    (segments.length === 3 && segments[1] === "contest") ||
    (segments.length === 3 && segments[1] === "event");

  const hasLogo = footerData.logo?.length > 0;
  const socialInfo = (footerData.social_info || []).flat().filter(Boolean);
  const contactInfo = (footerData.contact_info || []).flat().filter(Boolean);
  const copyright =
    footerData["copyright statement"] ||
    `© Copyright ${new Date().getFullYear()}. All Rights Reserved.`;
  const copyrightNotice = footerData["copyright notice"] || "";
  const creatorText = footerData.creator_text || null;

  // Social tagline: extract handle from first social URL
  const firstSocial = socialInfo[0];
  const socialHandle = firstSocial?.url
    ? "@" + firstSocial.url.replace(/\/$/, "").split("/").pop()
    : null;

  const hasCol2 = Boolean(
    footerData.column_2_title ||
    (footerData.column_2_links && footerData.column_2_links.length > 0),
  );
  const hasCol3 = Boolean(
    footerData.column_3_title ||
    (footerData.column_3_links && footerData.column_3_links.length > 0),
  );
  const hasCol4 = Boolean(
    footerData.column_4_title ||
    (footerData.column_4_links && footerData.column_4_links.length > 0),
  );
  const otherLinks = (footerData.other_links || [])
    .flat()
    .filter((l) => l?.label && l?.url);
  const hasContact = contactInfo.length > 0;

  return (
    <>
      {mobileAppCtaData && <MobileBanner data={mobileAppCtaData} />}

      <footer
        className={`${styles.footer} ${
          extraEventPadding ? styles.extraEventPadding : ""
        }`}
      >
        <Container>
          <div className={styles.footerGrid}>
            {/* ── Brand / Social Column ── */}
            <div className={styles.brandCol}>
              <Link href="/" aria-label="Home" className={styles.logoLink}>
                <Logo width={110} animated={true} />
              </Link>

              <SocialRow items={socialInfo} />

              {socialHandle && (
                <p className={styles.socialTagline}>
                  Don&apos;t follow us
                  <br />
                  <span>{socialHandle}</span>
                </p>
              )}
            </div>

            {/* ── Links & Contact Group ── */}
            <div className={styles.linksGroup}>
              {hasCol2 && (
                <FooterLinkColumn
                  title={footerData.column_2_title}
                  links={footerData.column_2_links}
                />
              )}

              {hasCol3 && (
                <FooterLinkColumn
                  title={footerData.column_3_title}
                  links={footerData.column_3_links}
                />
              )}

              {hasCol4 && (
                <FooterLinkColumn
                  title={footerData.column_4_title}
                  links={footerData.column_4_links}
                />
              )}

              {(hasContact || otherLinks.length > 0) && (
                <div className={styles.contactCol}>
                  <div className={styles.linkCol}>
                    <strong className={styles.colTitle}>Contact</strong>
                    <ContactRow items={contactInfo} />
                    {otherLinks.length > 0 && (
                      <div className="flex flex-col gap-1 mt-2">
                        {otherLinks.map((link, i) => (
                          <CMSLink
                            key={i}
                            config={link}
                            useIn="header"
                            className={styles.footerLink}
                            title={link.label}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Container>

        {/* ── Bottom bar ─────────────────────────────────── */}
        <div className={styles.bottomBar}>
          <Container>
            <div className={styles.bottomBarInner}>
              <span className={styles.copyright}>{copyright}</span>

              <div className={styles.madeWith}>
                {creatorText && (
                  <>
                    <span>Made With</span>
                    <Image
                      src={loveIcon}
                      alt="love"
                      width={18}
                      height={16}
                      className={styles.loveIcon}
                    />
                    <span>by</span>
                    <a
                      href="https://aavatto.com"
                      target="_blank"
                      rel={EXTERNAL_REL}
                      className={styles.aavattoLink}
                      aria-label="Aavatto"
                    >
                      <Image
                        src={aavattoLogo}
                        alt="Aavatto"
                        width={72}
                        height={20}
                        className={styles.aavattoLogo}
                      />
                    </a>
                  </>
                )}
              </div>
            </div>

            {copyrightNotice && (
              <p className={styles.copyrightNotice}>{copyrightNotice}</p>
            )}
          </Container>
        </div>
      </footer>
    </>
  );
}
